/**
 * Word emphasis from prosody — pure JS analyser running on the cached
 * Float32 PCM buffer (16 kHz mono, populated after first transcribe).
 *
 * What "emphasis" means here:
 *   When a speaker raises their voice, slows down on a word, or hits it
 *   with extra energy, that word stands out. Submagic / Captions.ai
 *   detect this and style the word differently (bigger / yellow / bold).
 *
 * Approach (no extra ML model — pure DSP):
 *   For each word [t0, t1] in seconds:
 *     1. Slice PCM → samples for the word.
 *     2. Compute three features:
 *        – RMS (loudness)
 *        – Spectral centroid (brightness — proxies enthusiasm/clarity)
 *        – Duration / char count ratio (slow-deliberate words = emphasis)
 *     3. Z-score against the per-clip mean+stddev.
 *     4. Weighted sum → emphasis score 0..1.
 *     5. Top ~12% of words → "high" emphasis, next ~25% → "med",
 *        rest → "normal". Quotas keep emphasis sparse — if everything is
 *        "high", nothing is.
 *
 * Output:
 *   Mutates each `words[i]` in-place with `emphasis: 'high' | 'med' | 'normal'`.
 *   Words that are emoji or pure punctuation are skipped.
 *
 * Why this isn't redundant with confidence:
 *   Confidence is "did Whisper get this right?" — about transcription.
 *   Emphasis is "did the speaker stress this?" — about delivery. Both are
 *   per-word annotations but capture different signals.
 */
(function(){
    'use strict';

    var SAMPLE_RATE = 16000;

    /* RMS energy of a Float32 slice. */
    function rms(arr) {
        if (!arr || !arr.length) return 0;
        var s = 0;
        for (var i = 0; i < arr.length; i++) s += arr[i] * arr[i];
        return Math.sqrt(s / arr.length);
    }

    /* Spectral centroid of the centre N samples of the word (N a power of
       two, 64..1024 — ≈64 ms at 16 kHz), Hann window.
       Centroid = Σ(magnitude * frequency) / Σ(magnitude) over bins 0..N/2−1.
       Higher centroid = brighter / more energy in higher harmonics = more
       "active" delivery.

       Radix-2 FFT with the twiddles and the window cached per N. Until
       01.10.2026 this was a direct DFT that called Math.cos AND Math.sin N²/2
       times per word — «trivial» on paper, 55–76 ms a word in the tab: 192–
       266 s of main-thread time for the 3 486 words of a 27-min lecture, the
       page frozen on «Recognizing speech 81 %» after every transcription
       (≈20–30 s on an average 163-s video). Same numbers — the direct DFT is
       kept as the oracle in tests/js/auto-captions-prosody-fft.test.mjs. */
    var FFT_TABLES = {};
    function fftTables(N) {
        var t = FFT_TABLES[N];
        if (t) return t;
        var bits = Math.round(Math.log(N) / Math.LN2);
        t = { cos: new Float64Array(N / 2), sin: new Float64Array(N / 2), hann: new Float64Array(N), rev: new Uint32Array(N) };
        for (var k = 0; k < N / 2; k++) { t.cos[k] = Math.cos(2 * Math.PI * k / N); t.sin[k] = Math.sin(2 * Math.PI * k / N); }
        for (var i = 0; i < N; i++) {
            t.hann[i] = 0.5 - 0.5 * Math.cos(2 * Math.PI * i / (N - 1));
            var r = 0;
            for (var b = 0, x = i; b < bits; b++, x >>= 1) r = (r << 1) | (x & 1);
            t.rev[i] = r;
        }
        return (FFT_TABLES[N] = t);
    }
    var FFT_WIN = new Float32Array(1024), FFT_RE = new Float64Array(1024), FFT_IM = new Float64Array(1024);
    function spectralCentroid(arr) {
        if (!arr || arr.length < 64) return 0;
        // Trim to power-of-2 length up to 1024
        var N = 1;
        while (N * 2 <= arr.length && N * 2 <= 1024) N *= 2;
        if (N < 64) return 0;
        var start = Math.floor((arr.length - N) / 2);
        var t = fftTables(N), re = FFT_RE, im = FFT_IM, win = FFT_WIN, cs = t.cos, sn = t.sin, hann = t.hann, rev = t.rev, i;
        // The windowed samples pass through float32, as the direct DFT's did.
        for (i = 0; i < N; i++) win[i] = arr[start + i] * hann[i];
        for (i = 0; i < N; i++) { re[rev[i]] = win[i]; im[i] = 0; }
        for (var size = 2; size <= N; size <<= 1) {
            var half = size >> 1, step = N / size;
            for (var i0 = 0; i0 < N; i0 += size) {
                for (var j = 0, k = 0; j < half; j++, k += step) {
                    var a = i0 + j, b = a + half, c = cs[k], s = sn[k];
                    // × e^{−2πik/N} = cos − i·sin
                    var tr = re[b] * c + im[b] * s;
                    var ti = im[b] * c - re[b] * s;
                    re[b] = re[a] - tr; im[b] = im[a] - ti;
                    re[a] += tr; im[a] += ti;
                }
            }
        }
        var magSum = 0, freqSum = 0;
        for (var q = 0; q < N / 2; q++) {
            var mag = Math.sqrt(re[q] * re[q] + im[q] * im[q]);
            magSum += mag;
            freqSum += mag * ((q * SAMPLE_RATE) / N);
        }
        return magSum > 1e-9 ? (freqSum / magSum) : 0;
    }

    /* Compute mean + stddev for a numeric array. */
    function stats(arr) {
        if (!arr.length) return { mean: 0, std: 1 };
        var s = 0;
        for (var i = 0; i < arr.length; i++) s += arr[i];
        var m = s / arr.length;
        var v = 0;
        for (var j = 0; j < arr.length; j++) {
            var d = arr[j] - m;
            v += d * d;
        }
        return { mean: m, std: Math.sqrt(v / arr.length) || 1 };
    }

    function isEligible(word) {
        if (!word || word.isEmoji) return false;
        if (word.isSoundCue) return false; // [applause] / [laughter] / [music] — render-only
        var t = String(word.text || '').replace(/[\W_]+/g, '');
        if (!t) return false;            // pure punctuation — skip
        if (t.length < 2) return false;  // single-letter words rarely emphasised
        return true;
    }

    /**
     * Analyse PCM + words → returns words array with .emphasis annotation.
     * Same array reference as input (mutated in place + returned).
     *
     * Quotas:
     *   topQ.high (default 0.12) — top 12% of eligible words → 'high'
     *   topQ.med  (default 0.25) — next 25%                   → 'med'
     *   rest                                                  → 'normal'
     */
    window.__acAnalyseProsody = function(pcmF32, sampleRate, words, opts) {
        opts = opts || {};
        var topHigh = opts.topHigh || 0.12;
        var topMed  = opts.topMed  || 0.25;
        var sr = sampleRate || SAMPLE_RATE;

        if (!pcmF32 || !pcmF32.length || !Array.isArray(words) || !words.length) return words;

        // Compute per-word features.
        var features = []; // {idx, score}
        var rmsList = [], centList = [], durList = [];
        var perWord = new Array(words.length);

        for (var i = 0; i < words.length; i++) {
            var w = words[i];
            if (!isEligible(w)) { perWord[i] = null; continue; }
            var s0 = Math.max(0, Math.floor(w.start * sr));
            var s1 = Math.min(pcmF32.length, Math.ceil(w.end * sr));
            if (s1 - s0 < 80) { perWord[i] = null; continue; } // < 5 ms — too short
            var slice = pcmF32.subarray(s0, s1);
            var f = {
                rms: rms(slice),
                cent: spectralCentroid(slice),
                durOverChars: (w.end - w.start) / Math.max(1, String(w.text).replace(/\W/g,'').length),
            };
            perWord[i] = f;
            rmsList.push(f.rms);
            centList.push(f.cent);
            durList.push(f.durOverChars);
        }

        var rmsS = stats(rmsList);
        var centS = stats(centList);
        var durS = stats(durList);

        // Combine into one z-score-style emphasis score.
        // RMS gets the heaviest weight — loudness is the strongest perceived
        // marker of emphasis. Spectral centroid second (brightness ≈ enthusiasm).
        // Duration third (slow words can be either thoughtful or hesitant).
        var W_RMS = 0.5, W_CENT = 0.3, W_DUR = 0.2;

        var scored = [];
        for (var k = 0; k < words.length; k++) {
            var pw = perWord[k];
            if (!pw) {
                words[k].emphasis = 'normal';
                continue;
            }
            var zR = (pw.rms - rmsS.mean) / rmsS.std;
            var zC = (pw.cent - centS.mean) / centS.std;
            var zD = (pw.durOverChars - durS.mean) / durS.std;
            var score = W_RMS * zR + W_CENT * zC + W_DUR * zD;
            scored.push({ idx: k, score: score });
        }

        // Sort descending by score, assign quotas.
        scored.sort(function(a, b){ return b.score - a.score; });
        var nHigh = Math.max(1, Math.round(scored.length * topHigh));
        var nMed  = Math.max(0, Math.round(scored.length * topMed));
        // If only a handful of eligible words, drop the high-emphasis quota
        // to 1 — otherwise we'd label half of a 4-word transcript as "high".
        if (scored.length <= 6) { nHigh = 1; nMed = 0; }

        for (var s = 0; s < scored.length; s++) {
            var label = (s < nHigh) ? 'high' : (s < nHigh + nMed ? 'med' : 'normal');
            words[scored[s].idx].emphasis = label;
            // Also record raw score for debugging / SRT customisation hooks.
            words[scored[s].idx].emphasisScore = +scored[s].score.toFixed(3);
        }

        return words;
    };

    // For tests/js — the centroid is held against the direct DFT it replaced.
    window.__acAnalyseProsody._centroid = spectralCentroid;

    /* Strip emphasis from all words — used when toggle is turned off. */
    window.__acClearProsody = function(words) {
        if (!Array.isArray(words)) return words;
        for (var i = 0; i < words.length; i++) {
            delete words[i].emphasis;
            delete words[i].emphasisScore;
        }
        return words;
    };
})();
